/* eslint-disable id-length */
// Lodash replacement utilities
// Each function mirrors the lodash API it replaces

function hasSeenPair(seen, val1, val2) {
  const seenPairs = seen.get(val1);
  return seenPairs ? seenPairs.has(val2) : false;
}

function addSeenPair(seen, val1, val2) {
  let seenPairs = seen.get(val1);
  if (!seenPairs) {
    seenPairs = new WeakSet();
    seen.set(val1, seenPairs);
  }
  seenPairs.add(val2);
}

export function isEqual(val1, val2, seen = new WeakMap()) {
  if (val1 === val2) return true;
  if (val1 == null || val2 == null) return val1 === val2;
  if (typeof val1 !== typeof val2) return false;
  if (Number.isNaN(val1) && Number.isNaN(val2)) return true;

  if (Array.isArray(val1)) {
    if (!Array.isArray(val2) || val1.length !== val2.length) return false;
    if (hasSeenPair(seen, val1, val2)) return true;
    addSeenPair(seen, val1, val2);
    return val1.every((val, idx) => isEqual(val, val2[idx], seen));
  }

  if (Array.isArray(val2)) return false;
  if (val1 instanceof Date && val2 instanceof Date) return val1.getTime() === val2.getTime();
  if (val1 instanceof RegExp && val2 instanceof RegExp) return val1.toString() === val2.toString();

  if (typeof val1 === 'object') {
    if (hasSeenPair(seen, val1, val2)) return true;
    addSeenPair(seen, val1, val2);

    const keysA = Object.keys(val1);
    const keysB = Object.keys(val2);
    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => Object.prototype.hasOwnProperty.call(val2, key) && isEqual(val1[key], val2[key], seen));
  }

  return false;
}

export function debounce(fn, wait) {
  let timer;
  const debounced = function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

export function pick(obj, keys) {
  if (obj == null) return {};
  if (!Array.isArray(keys)) keys = [keys];
  const result = {};
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key];
    }
  }
  return result;
}

export function omit(obj, keys) {
  if (obj == null) return {};
  const keySet = new Set(Array.isArray(keys) ? keys : [keys]);
  const result = {};
  for (const key of Object.keys(obj)) {
    if (!keySet.has(key)) {
      result[key] = obj[key];
    }
  }
  return result;
}

export function groupBy(arr, fn) {
  if (arr == null) return {};
  const iteratee = typeof fn === 'function' ? fn : (item) => item[fn];
  const result = Object.create(null);
  for (const item of arr) {
    const key = iteratee(item);
    if (!result[key]) result[key] = [];
    result[key].push(item);
  }
  return result;
}

export function sortBy(arr, fn) {
  if (arr == null) return [];
  const iteratee = typeof fn === 'function' ? fn : (item) => item[fn];
  return [...arr].sort((left, right) => {
    const va = iteratee(left);
    const vb = iteratee(right);
    if (va < vb) return -1;
    if (va > vb) return 1;
    return 0;
  });
}

export function uniqBy(arr, fn) {
  if (arr == null) return [];
  const iteratee = typeof fn === 'function' ? fn : (item) => item[fn];
  const seen = new Set();
  return arr.filter((item) => {
    const key = iteratee(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function pickBy(obj, fn = Boolean) {
  if (obj == null) return {};
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (fn(value, key)) {
      result[key] = value;
    }
  }
  return result;
}

export function mapValues(obj, fn) {
  if (obj == null) return {};
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = fn(value, key);
  }
  return result;
}

export function mapObject(obj, fn) {
  if (obj == null) return [];
  return Object.entries(obj).map(([key, value]) => fn(value, key));
}

export function zipObject(keys, values) {
  if (keys == null) return {};
  const normalizedValues = values == null ? [] : values;
  const result = {};
  for (let idx = 0; idx < keys.length; idx += 1) {
    result[keys[idx]] = normalizedValues[idx];
  }
  return result;
}

export function zip(...arrays) {
  if (arrays.length === 0) return [];
  const validArrays = arrays.map((arr) => (arr == null ? [] : arr));
  const length = Math.max(...validArrays.map((arr) => arr.length));
  return Array.from({ length }, (_, idx) => validArrays.map((arr) => arr[idx]));
}

export function partition(arr, fn) {
  if (arr == null) return [[], []];
  const pass = [];
  const fail = [];
  for (const item of arr) {
    (fn(item) ? pass : fail).push(item);
  }
  return [pass, fail];
}

export function camelCase(str) {
  return String(str)
    .replace(/[-_\s]+(.)?/g, (_, chr) => (chr ? chr.toUpperCase() : ''))
    .replace(/^[A-Z]/, (chr) => chr.toLowerCase());
}

export function upperFirst(str) {
  str = String(str);
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function capitalize(str) {
  str = String(str).toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isEmpty(val) {
  if (val == null) return true;
  if (typeof val === 'boolean' || typeof val === 'number') return true;
  if (typeof val === 'string' || Array.isArray(val)) return val.length === 0;
  if (val instanceof Map || val instanceof Set) return val.size === 0;
  if (typeof val === 'object') return Object.keys(val).length === 0;
  return false;
}

export function countBy(arr, fn) {
  if (arr == null) return {};
  const iteratee = typeof fn === 'function' ? fn : (item) => item[fn];
  const result = Object.create(null);
  for (const item of arr) {
    const key = iteratee(item);
    result[key] = (result[key] || 0) + 1;
  }
  return result;
}

let idCounter = 0;
export function uniqueId(prefix = '') {
  idCounter += 1;
  return `${prefix}${idCounter}`;
}

export function truncate(str, options = {}) {
  const { length = 30, omission = '...', separator } = options;
  str = String(str);
  if (str.length <= length) return str;

  let end = length - omission.length;
  if (end < 0) end = 0;

  let result = str.slice(0, end);
  if (separator) {
    const sepRegex = typeof separator === 'string' ? new RegExp(separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g') : separator;
    let lastIndex = -1;
    const searchStr = result;
    if (sepRegex.global) sepRegex.lastIndex = 0;
    let match = sepRegex.exec(searchStr);
    while (match !== null) {
      lastIndex = match.index;
      if (!sepRegex.global) break;
      match = sepRegex.exec(searchStr);
    }
    if (lastIndex > -1) result = result.slice(0, lastIndex);
  }
  return result + omission;
}

export function floor(num, precision = 0) {
  const shifted = Number(`${num}e${precision}`);
  return Number(`${Math.floor(shifted)}e${-precision}`);
}

export function ceil(num, precision = 0) {
  const shifted = Number(`${num}e${precision}`);
  return Number(`${Math.ceil(shifted)}e${-precision}`);
}

export function remove(arr, fn) {
  if (arr == null) return [];
  const removed = [];
  for (let idx = arr.length - 1; idx >= 0; idx -= 1) {
    if (fn(arr[idx], idx, arr)) {
      removed.unshift(arr.splice(idx, 1)[0]);
    }
  }
  return removed;
}

export function sum(arr) {
  if (arr == null) return 0;
  return arr.reduce((total, num) => total + num, 0);
}
